<template>
    <div class="box-border pb-16 xxl:max-w-[1920px]">
        <h1 class="font-body-6 text-default-2 xxs:pl-4 lg:pl-0">Resources</h1>
        <div class="xxs:p-4 bg-white rounded border border-white lg:px-6 lg:pt-4 lg:pb-6 mt-4 box-border relative">
            <div v-if="(isAdmin || isContentAdmin) && isMobile" class="fixed z-50 bottom-4 right-4 left-auto min-w-[60px] h-[60px] rounded-full text-white bg-primary-1 flex items-center justify-center py-2 px-4 box-border group transition-all ease-in-out duration-200 cursor-pointer hover:min-w-[120px]">
                <router-link :to="'/resources/create'" class="flex items-center whitespace-nowrap">
                    <span class="text-body-2 block -mr-2 group-hover:mr-0">+</span>
                    <p class="p-0 text-body-6 ml-2 mt-0 mb-0 mr-0 opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[100px] transition-all ease-in-out duration-200">Add resources</p>
                </router-link>
            </div>
            <div class="w-full flex flex-col box-border" v-if="loadingPage">
                <div class="w-full flex xxs:flex-col lg:flex-row mb-5">
                    <div class="skeleton-loader xxs:w-full lg:w-2/5 h-[42px]"></div>
                    <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/4 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    <div class="flex-1 flex">
                        <div class="skeleton-loader xxs:w-[49%] lg:w-[20%] h-[42px] xxs:ml-0 xxs:mt-4 lg:mt-0 lg:ml-4 xxs:mr-4"></div>
                        <div class="skeleton-loader flex-1 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    </div>
                </div>
                <div class="w-full flex box-border justify-between flex-wrap preloader-wrap">
                    <div class="skeleton-loader xxs:w-full lg:w-[calc(33%-10px)] mt-5 h-[430px]" v-for="item in perPage" :key="item"></div>
                </div>
                <div class="flex justify-between w-full mt-6">
                    <div class="w-1/5 skeleton-loader h-[42px]" v-for="i in 3" :key="i"></div>
                </div>
            </div>
            <div class="w-full flex flex-col" v-else>
                <div v-if="hasResources" class="w-full flex flex-col items-end xxs:flex-wrap xl:flex-nowrap">
                    <div class="flex w-full justify-between xxs:flex-col md:flex-row ">
                        <InputGroup
                            v-model="search"
                            class="xxs:w-full md:w-[calc(50%-8px)] lg:w-1/2 xxs:order-last md:order-1 xxs:mt-4 md:mt-0"
                            hasSearchIcon
                            label="Search"
                            placeholder="Enter article subject"
                            @input="handleDebouncedSearch()"
                        >
                            <div class="absolute inset-y-0 right-0 pr-3 flex items-center h-10 cursor-pointer">
                                <img v-if="!search" alt="Search through resources" src="@/assets/icons/search.svg"/>
                                <img v-else src="@/assets/icons/close-light.svg" @click="clearSearchInput" alt="clear"/>
                            </div>
                        </InputGroup>
                        <CoreButton v-if="(isAdmin || isContentAdmin) && !isMobile" buttonType="primary" class="xxs:w-full md:w-[160px] h-12 xxs:order-1 md:order-last" @click.prevent="router.push({name: 'create-resources'})">
                            <span>+</span>
                            <span class="ml-2.5">Add resources</span>
                        </CoreButton>
                    </div>
                    <div class="flex xxs:flex-col md:flex-row w-full mt-4">
                        <div class="flex flex-col xxs:w-full md:w-[calc(20%-8px)] xxs:mt-4 md:mt-0">
                            <p class="text-body-6 text-default-1 font-medium mb-1">Date</p>
                            <VueDatePicker
                                :model-value="selectedDate"
                                @update:model-value="handleDate"
                                :max-date="maxDateBasedOnRoleAndFilters"
                                range
                                mode-height="120"
                                auto-apply
                                placeholder="Select date"
                                :enable-time-picker="false"
                                model-type="yyyy-MM-dd"
                                class="mr-4"
                            />
                        </div>
                        <div v-if="isAdmin || isContentAdmin" class="flex flex-col xxs:w-full md:w-[calc(20%-8px)] xxs:mt-4 md:mt-0 md:ml-4">
                            <label class="text-body-6 font-medium text-default-1 mb-1">
                                Type
                            </label>
                            <Multiselect
                                mode="single"
                                :canDeselect="false"
                                placeholder="Select a type"
                                :classes="classes"
                                :options="types"
                                v-model="selectedType"
                                :closeOnSelect="false"
                                :hideSelected="false"
                                :clearOnSelect="false"
                                :canClear="false"
                                :searchable="true"
                                :can-deselect="true"
                                @select="handleDropdownChange"
                                @deselect="handleDropdownChange"
                            >
                                <template v-slot:option="{ option, isSelected }">
                                    <div class="w-5 h-5 -ml-1 flex-shrink-0">
                                        <Check :color="color.hex" class="h-5" v-if="isSelected(option)"/>
                                    </div>
                                    <span class="select-none relative -mb-px capitalize pl-2.5">
                                        {{ option.label }}
                                     </span>
                                </template>
                            </Multiselect>
                        </div>
                        <div v-if="isAdmin || isContentAdmin" class="flex flex-col xxs:w-full md:w-[calc(20%-8px)] xxs:mt-4 md:mt-0 md:ml-4">
                            <label class="text-body-6 font-medium text-default-1 mb-1">
                                Filters
                            </label>
                            <Multiselect
                                mode="single"
                                :canDeselect="false"
                                placeholder="Select a filter"
                                :classes="classes"
                                :options="filters"
                                v-model="selectedFilter"
                                :closeOnSelect="false"
                                :hideSelected="false"
                                :clearOnSelect="false"
                                :canClear="false"
                                :searchable="true"
                                :can-deselect="true"
                                @select="handleDropdownChange"
                                @deselect="handleDropdownChange"
                            >
                                <template v-slot:option="{ option, isSelected }">
                                    <div class="w-5 h-5 -ml-1 flex-shrink-0">
                                        <Check :color="color.hex" class="h-5" v-if="isSelected(option)"/>
                                    </div>
                                    <span class="select-none relative -mb-px capitalize pl-2.5">
                                        {{ option.label }}
                                     </span>
                                </template>
                            </Multiselect>
                        </div>
                        <div class="flex flex-col xxs:w-full md:w-[calc(20%-8px)] xxs:mt-4 md:-mt-1.5 md:ml-4">
                            <TagSelect2
                                :model-value="selectedTags"
                                @update:model-value="selectTags"
                                :total-pages="totalTagPages"
                                :options="tags"
                                :createOption="canCreateTags"
                                :infinite-loader="true"
                                :close-on-select="true"
                                label="Tags"
                                @search-options="searchTags"
                                @load-infinite="({ page, search }) => loadTags(page, search)"
                            />
                        </div>
                        <div class="flex flex-col xxs:w-full md:w-[calc(20%-8px)] xxs:mt-4 md:mt-0 md:ml-4">
                            <label class="text-body-6 font-medium text-default-1 mb-1">
                                Order by
                            </label>
                            <Multiselect
                                mode="single"
                                placeholder="Select order"
                                :classes="classes"
                                :options="orderBy"
                                v-model="selectedOrder"
                                value-prop="value"
                                label-prop="label"
                                :closeOnSelect="true"
                                :hideSelected="false"
                                :clearOnSelect="false"
                                :canClear="false"
                                :searchable="true"
                                :can-deselect="true"
                                @select="handleSearch"
                                @deselect="handleSearch"
                            />
                        </div>
                    </div>
                </div>
                <div class="w-full flex mt-6 flex-nowrap xxs:flex-wrap" v-if="categories">
                    <div v-for="(category,index) in categories"
                         :key="category.uid"
                         @click="handleSelectCategory(category)"
                         class="flex p-2 rounded border xxs:mt-2 mt-0"
                         :class="[selectedCategory !==  null && selectedCategory.uid === category.uid ? 'border-primary-1 text-primary-1 hover:text-default-1 font-medium' : 'border-interactive-9 hover:border-primary-1 text-default-1 hover:text-primary-1 cursor-pointer',
                            index !==0 && index !== categories.length && 'mx-2']">
                            <img :src="getCategoryIcon(category.name)" alt="category icon" class="h-5" />
                            <p class="text-body-6 capitalize mx-2.5">
                                {{ category.name }}
                            </p>
                    </div>
                </div>

                <div class="w-full flex box-border flex-wrap justify-between preloader-wrap" v-if="loadingResources">
                    <div class="skeleton-loader xxs:w-full lg:w-[calc(33%-10px)] mt-5 h-[430px]" v-for="item in perPage" :key="item"></div>
                </div>

                <div class="flex w-full flex-wrap mt-1 justify-between" v-if="!loadingResources && resources.length > 0">
                    <ResourceCard
                        v-for="(resource, index) in resources"
                        :resource="resource"
                        :index="index"
                        :key="resource.uid"
                        @toggle-like="toggleLike"
                        @view="() => router.push({name: 'view-resource', params: {uid: resource.uid}})"
                        @edit="editResource"
                        @delete="handleDelete"
                        @share="handleShare"
                    />
                    <div class="xxs:hidden lg:block lg:w-[calc(33%-10px)]"></div>
                </div>
                <div v-if="emptyState" class="w-full h-[650px] flex justify-center items-center flex-col">
                    <NoBlogs :color="color.hex" />
                    <p class="mt-8 text-body-4 text-default-1">{{ emptyStateText }}</p>
                    <CoreButton v-if="(isAdmin || isContentAdmin) && !isMobile" buttonType="ghost" class="mt-10" @click="addResources">
                        <img src="../../../assets/icons/plus.svg" alt="" />
                        <span class="ml-2.5">Add resources</span>
                    </CoreButton>
                </div>
                <div class="flex w-full flex-col">
                    <Pagination
                        v-if="showPagination"
                        :from="meta.from ?? 0"
                        :last-page="meta.last_page"
                        :links="links"
                        :to="meta.to ?? 0"
                        :total="meta.total ?? 0"
                        @change="handlePaginationChange"
                    />
                </div>
            </div>
        </div>
    </div>
    <Modal v-if="showDeleteModal" :show="showDeleteModal" @close="closeDeleteModal">
        <Delete
            v-model="showDeleteModal"
            @delete="doDeleteResource"
            @close="closeDeleteModal"
            :resource="resourceToBeDeleted"
        />
    </Modal>
    <Modal v-if="showShareModal" :show="showShareModal" @close="closeShareModal" :overflow-hidden="false" class="overflow-visible">
        <Share
            v-model="showShareModal"
            type="resource"
            :groups="resourceToBeShared.groups"
            :members="resourceToBeShared.members"
            :members-count="resourceToBeShared.members_count"
            :groups-count="resourceToBeShared.groups_count"
            :is-public-shared="resourceToBeShared.public"
            :is-global-shared="resourceToBeShared.global"
            @share="doShareResource"
        />
    </Modal>
</template>

<script setup>
import {storeToRefs} from "pinia";
import {useAuth} from "@/stores/AuthStore";
import {computed, nextTick, onBeforeMount, ref} from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import InputGroup from "../../../components/core/InputGroup.vue";
import CoreButton from "../../../components/core/Button.vue";
import TagSelect2 from "../../../components/core/TagSelect2.vue";
import debounce from "lodash.debounce";
import {useRouter} from "vue-router";
import NoBlogs from "../../../components/icons/NoBlogs.vue";
import Check from "../../../components/icons/Check.vue";
import {useIsMobile} from "@/composables/useIsMobile";
import Multiselect from "@vueform/multiselect";
import Pagination from "../../../components/core/Pagination.vue";
import {useResources} from "@/composables/useResources";
import {useTags} from "@/composables/useTags";
import {useTheme} from "@/stores/ThemeStore";
import ResourceCard from "../../../components/custom/resources/ResourceCard.vue";
import {toast} from "vue3-toastify";
import Modal from "@/components/core/Modal.vue";
import Share from "@/components/modals/partials/Share.vue";
import Delete from "@/components/modals/partials/Delete.vue";
import {useResourceCategories} from "@/composables/useResourceCategories";
import healthIcon from '@/assets/icons/resource-categories/health.svg';
import insuranceIcon from '@/assets/icons/resource-categories/insurance.svg';
import legalIcon from '@/assets/icons/resource-categories/legal.svg';
import generalIcon from '@/assets/icons/resource-categories/general.svg';
import miscIcon from '@/assets/icons/resource-categories/misc.svg';
import allIcon from '@/assets/icons/resource-categories/all.svg';
import benefitsIcon from '@/assets/icons/resource-categories/benefit.svg';
import retireesIcon from '@/assets/icons/resource-categories/people.svg';
import {useDomPurifier} from "@/composables/useDomPurifier";

const router = useRouter();

const {color} = useTheme();

const {getAllResources, deleteResource, shareResource} = useResources();
const {getResourceCategories} = useResourceCategories();
const {getTags} = useTags();

const {isAdmin, isContentAdmin} = useAuth();
const {organization} = storeToRefs(useAuth());
const {isMobile} = useIsMobile();

const search = ref('');
const types = ref([{label: 'All resources', value: ''}, {label: 'Page', value: 'page'}, {label: 'Link', value: 'weblink'},]);
const orderBy = ref([
    {label: 'Newest', value: {column: 'created_at', direction: 'desc'}},
    {label: 'Oldest', value: {column: 'created_at', direction: 'asc'}},
    {label: 'A-Z', value: {column: 'title', direction: 'asc'}},
    {label: 'Z-A', value: {column: 'title', direction: 'desc'}},
]);
const selectedOrder = ref({column: 'created_at', direction: 'desc'});
const selectedType = ref('');
const filters = ref([
    {label: 'All filters', value: 'all_resources'},
    {label: 'Favorite', value: 'liked'},
    {label: 'Draft', value: 'draft'},
    {label: 'Trashed', value: 'bin'}
]);
const selectedFilter = ref('');

const tags = ref([]);
const selectedTags = ref([]);
const totalTagPages = ref(0);
const loadingTags = ref(false);
const canCreateTags = ref(false);
const totalTags = ref(0);

const selectedDate = ref(null);

const loadingPage = ref(true);
const hasResources = ref(false);
const loadingResources = ref(false);

const selectedCategory = ref(null);
const categories = ref(null);
const resources = ref([]);

const meta = ref(null)
const links = ref([]);
const perPage = ref(15);
const page = ref(1);

const showShareModal = ref(false);
const showDeleteModal = ref(false);
const resourceToBeShared = ref(null);
const resourceToBeDeleted = ref(null);

const classes = ref({
    container: 'multiselect text-body-6 text-default-1 placeholder:text-default-4 bg-white border h-10 w-full rounded text-left focus:outline-none focus:ring-transparent focus:border-primary-1',
    search: 'w-full h-10 absolute border-0 inset-0 outline-none focus:ring-0 appearance-none box-border text-default-1 text-body-6 bg-transparent rounded pl-3.5 rtl:pl-0 rtl:pr-3.5 hover:border-primary-1 hover:border',
    option: 'flex items-center justify-start box-border text-left cursor-pointer leading-snug pl-4 pr-2 py-2 focus:text-primary-1 text-body-6 text-default-1',
    optionPointed: 'hover:text-primary-1 hover:bg-background-3',
    optionSelected: 'bg-background-3',
    optionDisabled: 'text-default-4 cursor-not-allowed',
    optionSelectedPointed: 'bg-background-3 opacity-90',
    optionSelectedDisabled: 'bg-background-3 opacity-70 cursor-not-allowed pointer-events-none',
});

function loadTags(page, searchQuery = '') {
    loadingTags.value = true;
    getTags(organization.value.slug, page, 10, searchQuery)
        .then((response) => {
            tags.value = [...tags.value, ...response.data.map(tag => tag.name)];
            totalTagPages.value = response.meta.last_page;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loadingTags.value = false;
        })
}

function searchTags(searchQuery) {
    getTags(organization.value.slug, 1, 10, searchQuery)
        .then((response) => {
            totalTagPages.value = response.meta.last_page;
            totalTags.value = response.meta.total;

            if (totalTags.value === 0) {
                nextTick(() => {
                    canCreateTags.value = true;
                });
                tags.value = [];
                return;
            }

            tags.value = response.data.map((tag) => {
                return tag.name;
            });
        })
        .catch((error) => {
            console.error(error);
        });
}

const handleDebouncedSearch = debounce(handleSearch, 300);

async function handleSearch() {
    loadingResources.value = true;

    const response = await getAllResources(organization.value.slug, page.value, perPage.value, search.value, startDate.value, endDate.value, selectedType.value, selectedTags.value, selectedFilter.value, selectedOrder.value.column, selectedOrder.value.direction, selectedCategory.value && selectedCategory.value.uid);
    resources.value = response.data;
    meta.value = response.meta;

    loadingResources.value = false;

    meta.value = response.meta;
    links.value = response.links;
}

function clearSearchInput () {
    search.value = '';
    handleSearch();
}

function handleDate(date) {
    selectedDate.value = date;
    handleSearch();
}

function handleDropdownChange() {
    handleSearch();
}

function selectTags(tags) {
    selectedTags.value = tags
    canCreateTags.value = false;
    handleSearch();
}

function addResources() {
    router.push({name: 'create-resources'});
}

function editResource({type, uid}) {
    if(type === 'weblink') {
        return router.push({name: 'edit-external-link', params: {uid: uid}});
    }
    router.push({name: 'edit-page', params: {uid: uid}});
}

async function doDeleteResource() {
    try {
        await deleteResource(organization.value.slug, resourceToBeDeleted.value.uid)
        resources.value = resources.value.filter((resource) => resource.uid !== resourceToBeDeleted.value.uid);

        toast.success("The resource was deleted successfully.");
    } catch (e) {
        console.log(e);
        toast.error("There was an error trying to delete the resource.");
    } finally {
        closeDeleteModal();
    }
}

function handleShare(resource) {
    showShareModal.value = true;
    resourceToBeShared.value = resource;
}

function handleDelete(resource) {
    showDeleteModal.value = true;
    resourceToBeDeleted.value = resource;
}

function closeDeleteModal() {
    showDeleteModal.value = false;
    resourceToBeDeleted.value = null;
}

function closeShareModal() {
    showShareModal.value = false;
    resourceToBeShared.value = null;
}

async function doShareResource(data) {
    try {
        const response = await shareResource(organization.value.slug, resourceToBeShared.value.uid, data);

        toast.success("The resource was shared successfully.");

        resources.value = resources.value.map((resource) => {
            if(resource.uid === resourceToBeShared.value.uid) {
                return {
                    ...resource,
                    ...response
                }
            } else {
                return resource;
            }
        });
    } catch (e) {
        console.log(e);
        toast.error("There was an error trying to share the resource.");
    } finally {
        showShareModal.value = false;
        resourceToBeShared.value = null;
    }
}

function toggleLike(index) {
    resources.value[index].isLiked = !resources.value[index].isLiked;
}

const handlePaginationChange = ({page: newPage, perPage: newPerPage}) => {
    page.value = newPage;
    perPage.value = newPerPage;
    handleSearch();
}

const handleSelectCategory = (category) => {
    if(selectedCategory.value && selectedCategory.value.uid === category.uid) {
        selectedCategory.value = categories.value.find((category) => category.uid === null);
    } else {
        selectedCategory.value = category;
    }
    handleSearch();
}

const emptyState = computed(() => {
    return resources.value.length === 0 && !loadingResources.value;
});

const emptyStateText = computed(() => {
    return hasResources.value ? 'We couldn\'t find any resources based on the searched filters' : 'There are no resources added yet';
});

const showPagination = computed(() => {
    return (!loadingPage.value && hasResources.value && !emptyState.value && meta.value !== null);
});

const startDate = computed(() => {
    return selectedDate.value ? selectedDate.value[0] : '';
});

const endDate = computed(() => {
    return selectedDate.value ? selectedDate.value[1] : '';
});

const maxDateBasedOnRoleAndFilters = computed(() => {
    if(!isAdmin && !isContentAdmin) {
        return new Date();
    }

    let filterWithNoFutureDateSelection = ['published'];

    return !filterWithNoFutureDateSelection.includes(selectedType.value) ? null : new Date();
});

const getCategoryIcon = (categoryName) => {
    switch (categoryName) {
    case 'Health':
        return healthIcon;
    case 'Benefits':
        return benefitsIcon;
    case 'Insurance':
        return insuranceIcon;
    case 'Legal':
        return legalIcon;
    case 'General':
        return generalIcon;
    case 'Misc':
        return miscIcon;
    case 'Retirees':
        return retireesIcon;
    default:
        return allIcon;
    }
};

onBeforeMount(async () => {
    loadTags(1);

    const response = await getAllResources(organization.value.slug, page.value, perPage.value, search.value, startDate.value, endDate.value, selectedType.value, selectedTags.value);

    resources.value = response.data;
    resources.value = resources.value.map(resource => {
        resource.resourceable.title = useDomPurifier(resource.resourceable.title);
        return resource;
    });
    meta.value = response.meta;
    links.value = response.links;

    categories.value = await getResourceCategories(organization.value.slug);
    categories.value.unshift({uid: null, name: 'All categories'});
    selectedCategory.value = categories.value.find((category) => category.uid === null);

    loadingPage.value = false;
    if(resources.value.length > 0) {
        hasResources.value = true;
    }
})
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style lang="scss">
.multiselect {
    border-color:#EFF2F7;
    &.is-active {
        border: 1px solid var(--primary-color);
        box-shadow: none;
    }

    &.is-disabled {
        background: white;
        .multiselect-caret {
            display: none;
        }
    }
}

.multiselect-dropdown {
    max-height: 400px;
}

.multiselect-caret, .multiselect-clear-icon {
    z-index: 1;
}

.white-inset {
    -webkit-box-shadow: inset 0 0 0 10px #FFFFFF;
    box-shadow: inset 0 0 0 10px #FFFFFF;
}

.multiselect-inifite-spinner, .multiselect-spinner {
    background-color: var(--primary-color);
}
</style>
